import { type ComponentChildren, type VNode, createElement } from "preact";
import { type JSX } from "preact";
import { defaultIconContext, IconContext } from "./iconContext.ts";


const CAMEL_PROPS =
  /^(?:accent|alignment|arabic|baseline|cap|clip(?!PathU)|color|dominant|fill|flood|font|glyph(?!R)|horiz|image(!S)|letter|lighting|marker(?!H|W|U)|overline|paint|pointer|shape|stop|strikethrough|stroke|text(?!L)|transform|underline|unicode|units|v|vector|vert|word|writing|x(?!C))[A-Z]/;
const CAMEL_REPLACE = /[A-Z0-9]/g;

function filterKebabCase<T extends Record<string, any>>(attrs: T): T {
  const newAttrs: Record<string, any> = {};
  for (const key in attrs) {
    if (key.indexOf('-') === -1 && CAMEL_PROPS.test(key))
      newAttrs[key.replace(CAMEL_REPLACE, '-$&').toLowerCase()] = attrs[key];
    else
      newAttrs[key] = attrs[key];
  }
  return newAttrs as T;
}

/**
 * tree level for SVG
 */
export interface IconTree {
  tag: string;
  attr: { [key: string]: string };
  child?: IconTree[];
}

/**
 * recursivly build internal SVG element
 * @param tree 
 * @returns 
 */
function Tree2Element(tree: IconTree[]): ComponentChildren[] { // React.ReactElement => ComponentChildren
  return (
    tree &&
    tree.map((node, i) =>
      createElement(
        node.tag,
        { key: i, ...filterKebabCase(node.attr) },
        Tree2Element(node.child || []),
      )
    )
  );
}

/**
 * build a SVG componant from an IconTree
 */
export function GenIcon(data: IconTree) {
  return (props: IconBaseProps) => (
    <IconBase attr={{ ...data.attr }} {...props}>
      {Tree2Element(data.child || [])}
    </IconBase>
  );
}

/**
 * attribut for all custom SVG
 */
export interface IconBaseProps extends JSX.SVGAttributes<SVGSVGElement> {
  children?: ComponentChildren; // was React.ReactNode
  size?: number; // was string | number;
  color?: string;
  title?: string;
  class?: string; // new
}

export type IconType = (props: IconBaseProps) => VNode<JSX.SVGAttributes>;

/**
 * build outer SVG element
 */
export function IconBase(
  props: IconBaseProps & { attr?: Record<string, string> },
): VNode<JSX.SVGAttributes> {
  const elem = (conf: IconContext) => {
    const { attr, size, title, ...svgProps } = props;
    let clazz = props.class || '';
    const computedSize = size || conf.size || "1em";
    if (conf.class) {
      if (clazz)
        clazz = `${clazz} ${conf.class}`;
      else
        clazz = conf.class;
    }
    let attrs = {
      stroke: conf.stroke || "currentColor",
      fill: conf.fill || "currentColor",
      strokeWidth: conf.strokeWidth || 0,
      class: clazz,
      ...conf.attr,
      ...attr,
      ...svgProps,
      height: computedSize,
      width: computedSize,
    };
    attrs = filterKebabCase(attrs);
    return (
      <svg
        {...attrs}
        style={filterKebabCase({
          color: props.color || conf.color,
          ...conf.style,
          ...(props.style as JSX.CSSProperties),
        })}
        xmlns="http://www.w3.org/2000/svg"
      >
        {title && <title>{title}</title>}
        {props.children}
      </svg>
    );
  };

  return defaultIconContext !== undefined
    ? (
      <defaultIconContext.Consumer>
        {(conf: IconContext) => elem(conf)}
      </defaultIconContext.Consumer>
    )
    : (
      elem(defaultIconContext)
    );
}

// denoCacheMetadata={"headers":{"x-jsd-version":"1.0.8","access-control-expose-headers":"*","x-cache":"HIT, MISS","alt-svc":"h3=\":443\"; ma=86400","x-jsd-version-type":"version","cf-ray":"8e29c0e54abf9464-SJC","age":"0","etag":"W/\"d09-LoU0DQ7zrOY2n0QBFPdDGHiFoLY\"","content-type":"text/plain; charset=utf-8","accept-ranges":"bytes","report-to":"{\"endpoints\":[{\"url\":\"https:\\/\\/a.nel.cloudflare.com\\/report\\/v4?s=95POtDXJRHikvKNMm%2FvgQFL0ahhcdEmA8qzqcIhmYe477luFheZPs2iVjFVbhmL69DP93mjmqiXvnsnxIizM2D7HBouHBWj%2FQwShB9%2BNKYnKJtNJqkaE7MTl6W5%2BkNf%2BeJo%3D\"}],\"group\":\"cf-nel\",\"max_age\":604800}","timing-allow-origin":"*","access-control-allow-origin":"*","cross-origin-resource-policy":"cross-origin","x-content-type-options":"nosniff","nel":"{\"success_fraction\":0.01,\"report_to\":\"cf-nel\",\"max_age\":604800}","x-served-by":"cache-fra-etou8220037-FRA, cache-lga21970-LGA","cache-control":"public, max-age=31536000, s-maxage=31536000, immutable","strict-transport-security":"max-age=31536000; includeSubDomains; preload","vary":"Accept-Encoding","cf-cache-status":"HIT","server":"cloudflare","date":"Thu, 14 Nov 2024 20:29:24 GMT"},"url":"https://cdn.jsdelivr.net/gh/urielch/react-icons@1.0.8/lib/iconBase.tsx","time":1731616164}